<template>
  <!-- N.B. since the element is whitespace: pre-wrap, all the children must be on one line of html to prevent leading line break-->
  <div class="rich-text-editor">
    <input :id="uniqueId" type="hidden" :value="value" /><trix-editor
      ref="editor"
      :toolbar="toolbarId"
      :input="uniqueId"
      :placeholder="placeholder"
      :dir="dir"
      @paste="autolinkPastedUrl"
      @trix-initialize="onInitialize"
      @trix-change="onInput"
      @trix-selection-change="onSelectionChange"
      @trix-attributes-change="onAttributesChange"
      @trix-file-accept="onFileAccept"
      @trix-attachment-add="onAttachmentAdd"
      @focusin="onFocusIn"
      @trix-focus="onFocus"
      @trix-blur="onBlur"
      @keydown.enter.exact="onEnter"
    />
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import uniqueId from 'lodash.uniqueid'
import Trix from 'trix'

const urlRegex = new RegExp(
  '^(https?:\\/\\/(?:www\\.|(?!www))[^\\s\\.]+\\.[^\\s]{2,}|www\\.[^\\s]+\\.[^\\s]{2,})$',
  'ig',
)

Trix.config.textAttributes.sup = {
  tagName: 'sup',
  inheritable: true,
}

Trix.config.textAttributes.sub = {
  tagName: 'sub',
  inheritable: true,
}

Trix.config.textAttributes.mark = {
  tagName: 'mark',
  inheritable: true,
}

Trix.config.blockAttributes.math = {
  tagName: 'var',
  terminal: true,
  text: {
    plaintext: true,
  },
}
// emitted events: initialize, focus, selectionchange, attributeschange, input [html]
export default {
  props: ['placeholder', 'value', 'dir', 'toolbarId'],
  data() {
    return {
      editor: null,
      uniqueId: uniqueId('rte'),
    }
  },
  watch: {
    value(newValue) {
      if (this.editor && this.html() !== newValue) {
        this.editor.loadHTML(newValue)
      }
    },
  },
  methods: {
    freezeSelection() {
      this.editor.activateAttribute('frozen')
      this.frozenSelectionRange = this.editor.getSelectedRange()
    },
    defreezeSelection() {
      if (this.frozenSelectionRange) {
        this.editor.setSelectedRange(this.frozenSelectionRange)
        this.frozenSelectionRange = null
      }
      this.editor && this.editor.deactivateAttribute('frozen')
    },
    focus() {
      // nextTick - ensures that the input element has completed rendering in the DOM
      // setTimeout -  defers the focusing event until after the current event propagation.
      //
      // If you click on a button that triggers this method and don't have setTimeout,
      // what can happen is: focus is shifted to this input, then immediately shifted to the button.
      this.$nextTick(() => setTimeout(() => this.$refs.editor.focus()))
    },
    activateAttribute(name, val) {
      this.defreezeSelection()
      this.editor.activateAttribute(name, val)
      if (name === 'href') {
        this.moveCursorToEndOfSelection()
      }
    },
    deactivateAttribute(name) {
      this.editor.deactivateAttribute(name)
    },
    moveCursorToEndOfSelection() {
      const currentSelectedRange = this.editor.getSelectedRange()
      if (currentSelectedRange && currentSelectedRange[0] !== currentSelectedRange[1]) {
        this.editor.setSelectedRange(currentSelectedRange[1])
      }
    },
    html() {
      return this.$refs.editor.value
    },
    activeAttributes() {
      return this.editor.composition.currentAttributes
    },
    selectedText() {
      if (!this.editor) return ''
      if (!this.editor.getDocument) return ''
      const currentSelection = this.editor.getSelectedRange()
      if (!currentSelection) return ''
      return this.editor.getDocument().getStringAtRange(currentSelection)
    },
    onInitialize(event) {
      this.editor = event.target.editor
      this.editor.loadHTML(this.value)
      this.$emit('initialize')
    },
    onFocus() {
      this.$emit('focus')
    },
    onBlur() {
      this.$emit('blur')
    },
    onFocusIn() {
      this.defreezeSelection()
    },
    onSelectionChange() {
      this.$emit('selectionchange', this.selectedText())
    },
    onAttributesChange() {
      this.$emit('attributeschange', this.activeAttributes())
    },
    onInput() {
      this.$emit('input', this.html())
    },
    onFileAccept(e) {
      this.$emit('fileaccept', e)
    },
    onEnter() {
      this.$emit('enter')
    },
    onAttachmentAdd(e) {
      if (e.attachment) {
        e.attachment.remove()
      }

      // If jQuery
      if (e.originalEvent && e.originalEvent.attachment) {
        e.originalEvent.attachment.remove()
      }
    },
    autolinkPastedUrl(e) {
      setTimeout(() => {
        const clip = e.clipboardData
        let pastedText = null
        if (clip && typeof clip.getData === 'function') {
          pastedText = clip.getData('Text')
        }
        if (pastedText) {
          if (pastedText.match(urlRegex)) {
            const element = e.currentTarget
            const editor = element.editor
            const currentText = editor.getDocument().toString()
            const currentSelection = element.editor.getSelectedRange()
            // text up to the cursor position
            const textWeAreInterestedIn = currentText.substring(0, currentSelection[0])
            // search for the start of the url
            const startOfPastedText = textWeAreInterestedIn.lastIndexOf(pastedText)
            // add an undo entry so people can undo the autolinking
            editor.recordUndoEntry('Auto Link Paste')
            // select the url text
            editor.setSelectedRange([startOfPastedText, currentSelection[0]])
            // add a hyperlink to it
            editor.activateAttribute('href', pastedText)
            // go back to the original selection
            return editor.setSelectedRange(currentSelection)
          }
        }
        return true
      }, 0)
    },
  },
}
</script>

<style lang="scss" scoped>
trix-editor {
  min-height: 24px;
}
trix-editor:empty:not(:focus)::before {
  color: rgba(0, 0, 0, 0.38);
}
[data-color-scheme='dark'] trix-editor:empty:not(:focus)::before {
  color: rgba(255, 255, 255, 0.5);
}
</style>
